import {Component, effect, Inject, Signal, TrackByFunction} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {Mutation, StartWorkflowVodInput} from '../../../types-workflow';

import {START_WORKFLOW_VOD_DEFAULT} from '../../../queries-workflow';
import {forkJoin} from 'rxjs';
import {Router} from '@angular/router';
import {VodRepackagingFormatSpecificComponent} from '../vod-repackaging-format-specific/vod-repackaging-format-specific.component';
import {BasicPoitemVodInfoComponent} from '../basics-poitem-vod-info/basic-poitem-vod-info.component';
import {clean_datetime} from '../../utils';
import {PO_DESTINATIONS, PoDestination} from '../../../target_systems';
import {Organization} from '../../../types-repository';
import {Po2024Service} from '../../../services/po-2024.service';

import {MessageFieldComponent} from '../../do-button/message-field.component';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {ContextService} from '../../../services/context.service';
import {ProcessService} from '../../../services/process.service';


// Same S3 delivery option the display-interactive workflow offers (see
// VodDisplayInteractivePoComponent), so repackaged output can be delivered to Display
// Interactive from any organization. It is the default destination for repackaging.
const DISPLAY_INTERACTIVE_DESTINATION: PoDestination = {
  display_name: 'Display Interactive (S3)',
  destination_id: 'displayinteractive'
};
const DEFAULT_CDN_DESTINATION: PoDestination = {display_name: 'Default CDN', destination_id: 'vod'};

@Component({
  selector: 'app-vod-repackaging-po',
  templateUrl: './vod-repackaging-po.component.html',
  styleUrls: ['./vod-repackaging-po.component.css'],
  imports: [
    ReactiveFormsModule,
    BasicPoitemVodInfoComponent,
    VodRepackagingFormatSpecificComponent,
    MessageFieldComponent,
    DoButtonComponent
]
})
export class VodRepackagingPoComponent {

  organization: Signal<Organization>;
  poDestinations: PoDestination[] = []
  poFormGroup: FormGroup;

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  constructor(
    private apollo: Apollo,
    @Inject('vod_repackaging') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    private banner: BannerService,
    contextService: ContextService,
    private processService: ProcessService,
    private router: Router
  ) {
    this.organization = contextService.organization;

    this.poFormGroup = formBuilder.group({
      po_number: [clean_datetime(new Date()), Validators.required],
      po_destination: [null, Validators.required],
      auto_publish: [true, Validators.required],
      poItems: formBuilder.array([]),
    });

    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        savedForm.poItems.forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(poItem)));
        this.poFormGroup.patchValue(savedForm, {emitEvent: false})
      }
    });

    effect(() => {
      const org = this.organization();
      if (!org) return;

      // Display Interactive is the pre-selected default (it is what repackaging is used for), the
      // Default CDN comes next, and the organization's own destination — if it has one that is not
      // already Display Interactive — is offered last.
      const ownDestination: PoDestination =
        PO_DESTINATIONS.hasOwnProperty(org.legacy_name) && org.legacy_name !== DISPLAY_INTERACTIVE_DESTINATION.destination_id
          ? {display_name: org.legacy_name, destination_id: org.legacy_name}
          : null;

      this.poDestinations = [
        DISPLAY_INTERACTIVE_DESTINATION,
        DEFAULT_CDN_DESTINATION,
        ...(ownDestination ? [ownDestination] : []),
      ];
      this.poFormGroup.controls['po_destination'].setValue(DISPLAY_INTERACTIVE_DESTINATION.destination_id);
    });

    this.poService.poItemToAdd$.subscribe(data => {
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(
          Object.assign(
            {},
            {
              basics: BasicPoitemVodInfoComponent.DEFAULTS,
              format_specific_data: VodRepackagingFormatSpecificComponent.DEFAULTS
            },
            data)))
      }
    });
    this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    });
  }

  compare_destination_id(o1: any, o2: any) {
    return o1?.destination_id === o2?.destination_id;
  }

  submit_po(button: DoButtonComponent) {
    const organization = this.organization()
    if (!this.poFormGroup.valid) {
      button.done('Form Invalid. Check for errors.', {
        severity: 'cl-alert-danger'
      })
      return
    }
    const workflows = []
    for (const poItem of this.poFormGroup.value.poItems) {
      workflows.push(this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_VOD_DEFAULT,
        variables: {
          input_brefix: poItem.basics.input_brefix,
          po_item_id: poItem.basics.po_item_id.trim(),
          po_name: `${organization.id}_${this.poFormGroup.value.po_number.trim()}`,
          po_destination: this.poFormGroup.value.po_destination,
          vtk_template: 'vod-display-interactive-repack',
          auto_publish: this.poFormGroup.value.auto_publish,
          format_specific_data: JSON.stringify(poItem.format_specific_data)
        } satisfies StartWorkflowVodInput

      }).pipe(
        map(d => ({
          process: d.data.start_workflow_vod_default,
          po_item_id: poItem.basics.po_item_id.trim()
        }))))
    }

    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('All encodes started successfully.');
          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} encoded.`,
              failureMessage: `Failed to encode ${workflow.po_item_id}.`,
              link: ['/#/o', organization.id, 'workflows',
                organization.id,
                `${organization.id}_${this.poFormGroup.value.po_number}`].join('/')
            })
          })
        },
        error: err => {
          button.done('Workflow submission failed: ' + err, {severity: 'cl-alert-danger'});
        }
      }
    )
  }

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.value)
  }

}
